import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMount } from 'ahooks';
import Head from 'next/head';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { ThemeProvider } from '@material-ui/core/styles';
import Router, { useRouter } from 'next/router';
import NProgress from 'nprogress';
import { SnackbarProvider } from 'material-ui-snackbar-provider';
import { LocalizationProvider } from '@material-ui/pickers';
import DayjsUtils from '@material-ui/pickers/adapter/dayjs';
import { AppProvider } from '../context/app.state';
import Page from '../@layouts/Page';
import Blank from '../@layouts/Blank';
import Login from '../@layouts/Login';
import { theme } from '../assets/theme';
import AppLoading from '../@components/AppLoading';
import CustomSnackbar from '../@components/CustomSnackbar';
import app from '../utils/feathers';
import i18n from '../locales/i18n';
import '../utils/firebase';
import '../assets/styles.css';
import 'nprogress/nprogress.css';
import 'dayjs/locale/en';
import 'dayjs/locale/th';

// Binding events.
Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());

const MyApp = (props) => {
  const {
    Component, pageProps, language,
  } = props;

  i18n.changeLanguage(language);

  const { t } = useTranslation();
  const router = useRouter();
  const { pathname } = router;
  const [mounted, setMounted] = useState(false);

  const initial = async () => {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('auth')) {
      try {
        await app.reAuthenticate();
        setMounted(true);
      } catch (err) {
        setMounted(true);
      }
    } else {
      setMounted(true);
    }
  };

  useMount(() => {
    initial();
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const jssStyles = window.document.querySelector('#jss-server-side');
      if (jssStyles) {
        jssStyles.parentElement.removeChild(jssStyles);
      }
    }
  }, []);

  const isBlankLayout = [
    '/logout',
  ].indexOf(pathname) !== -1 || (
    typeof Component === 'function' && Component.name === 'Custom404'
  );
  const isLoginLayout = [
    '/login',
    '/user_activate',
    '/forgot_password',
  ].indexOf(pathname) !== -1;
  const isPageLayout = !isBlankLayout && !isLoginLayout;

  return (
    <>
      <AppProvider>
        <I18nextProvider i18n={i18n}>
          <ThemeProvider theme={theme}>
            <LocalizationProvider
              dateAdapter={DayjsUtils}
              locale={i18n.language}
            >
              <SnackbarProvider
                SnackbarProps={{
                  autoHideDuration: 6000,
                  anchorOrigin: { vertical: 'top', horizontal: 'center' },
                }}
                SnackbarComponent={CustomSnackbar}
              >
                <Head>
                  <title>{t('app_name') || ''}</title>
                  <meta
                    name="description"
                    content={
                      i18n.language === 'th'
                        ? 'ION ENRGY แพลตฟอร์มที่ให้คุณเข้าถึงข้อมูลการผลิตไฟฟ้าจากพลังงานแสงอาทิตย์อย่างละเอียด รวมถึงการใช้พลังงานของคุณ ได้อย่างสะดวก แบบเรียลไทม์ ทุกที่ ทุกเวลา'
                        : 'ION ENERGY, the solar monitoring platform for ION ENERGY customers to access your real-time solar production and consumption at anywhere and anytime.'
                    }
                  />
                  <meta
                    name="viewport"
                    content="minimum-scale=1, initial-scale=1, width=device-width"
                  />
                  <meta name="theme-color" content={theme.palette.primary.main} />
                  <meta name="robots" content="noindex" />
                </Head>
                <AppLoading />
                {
                  isBlankLayout && mounted && (
                    <Blank>
                      <Component
                        {...pageProps}
                      />
                    </Blank>
                  )
                }
                {
                  isLoginLayout && mounted && (
                    <Login>
                      <Component
                        {...pageProps}
                      />
                    </Login>
                  )
                }
                {
                  isPageLayout && mounted && (
                    <Page>
                      <Component
                        {...pageProps}
                      />
                    </Page>
                  )
                }
              </SnackbarProvider>
            </LocalizationProvider>
          </ThemeProvider>
        </I18nextProvider>
      </AppProvider>
    </>
  );
};

// MyApp.getInitialProps = async ({ ctx }) => {
//   const i18nTest = '';
//   return {
//     req: {
//       i18n: {
//         language: ctx.req ? ctx.req.i18n.language : 'en',
//       },
//     },
//   };
// };

MyApp.defaultProps = {
  pageProps: {},
  store: {
    getState: () => { },
    subscribe: () => { },
    dispatch: () => { },
  },
  language: null,
};
MyApp.propTypes = {
  Component: PropTypes.any.isRequired,
  pageProps: PropTypes.object,
  store: PropTypes.object,
  language: PropTypes.string,
};

export default MyApp;
